import type { FormikProps } from 'formik'
import type { FunctionComponent } from 'react'

import { FlowPage } from '@monthio/design-system'
import {
	eventHubService,
	LoadingScreen,
	Region,
	trackingService,
} from '@monthio/react-components'
import { useDemoProfile } from '#hooks/useDemoProfile'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { QuestionnaireStepWithType } from './questionnaireUtils'
import type {
	DefaultConsumerInputs,
	ModuleDefinitionQuestionnaireType,
} from './types/questionnaire.types'

import { budgetApiClient } from '../../api/budgetApiClient'
import {
	isQuestionnaireModuleDefinition,
	ModuleDefinition,
} from '../../types/authenticateSmartcheckSessionDto'
import { ClientInputsType } from '../../types/clientInputs'
import { ConsumerInputError } from './Components/ConsumerInputError/ConsumerInputError'
import {
	getNegativeValue,
	getQuestionnaireStep,
	getSectionFromQuestionnaireType,
	isLastStepInSection,
} from './questionnaireUtils'
import {
	CarStatus,
	CivilStatus,
	EmploymentStatus,
	ModuleDefinitionQuestionnaire,
} from './types/questionnaire.types'
import { useQuestionnaireModule } from './useQuestionnaireModule'

const renderStep = (
	steps: (({
		formProps,
		regionId,
		isValidating,
	}: {
		formProps: FormikProps<DefaultConsumerInputs>
		regionId: Region
		isValidating: boolean
	}) => JSX.Element)[],
	step: number,
	formProps: FormikProps<DefaultConsumerInputs>,
	regionId: Region,
	isValidating: boolean,
) => {
	const CurrentStep = steps[step]

	if (!CurrentStep) {
		return null
	}

	return (
		<CurrentStep
			regionId={regionId}
			formProps={formProps}
			isValidating={isValidating}
		/>
	)
}

export const Questionnaire: FunctionComponent = ({}) => {
	const { t } = useTranslation()
	const [isValidating, setIsValidating] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [isFailure, setIsFailure] = useState(false)
	const demoProfile = useDemoProfile()
	const [initialCpr, setInitialCpr] = useState<string>('')
	const { finish, config } = useQuestionnaireModule()

	useEffect(() => {
		const fetchData = async (demoProfileName: string) => {
			try {
				const demoProfiles = await budgetApiClient.fetchDemoProfiles()
				const cpr = demoProfiles.find((x) => x.name === demoProfileName)?.cpr

				setInitialCpr(cpr ?? '')
			} catch (error) {
				console.error('Error fetching demo profile cpr:', error)
			}
		}

		if (demoProfile !== null) {
			fetchData(demoProfile)
		}
	}, [demoProfile])

	let initialFormData: DefaultConsumerInputs = {
		idDocumentNationalityExemptions: config.idDocumentNationalityExemptions,
	}

	if (
		config.moduleIds.some(
			(x) =>
				x === ModuleDefinition.PersonalInformationQuestionnaire ||
				x === ModuleDefinition.PersonalInformationExtendedQuestionnaire ||
				x === ModuleDefinition.PersonalInformationCprOnlyQuestionnaire,
		)
	) {
		initialFormData = {
			...initialFormData,
			personalInformation: {
				cprNumber: initialCpr,
				phoneNumber: '',
				email: '',
				nationalities: [],
			},
		}
	}

	const [step, setStep] = useState(0)

	useEffect(() => {
		config.setConsumerInputsStepIndex(step)
	}, [config.setConsumerInputsStepIndex, step])

	if (config.moduleIds.includes(ModuleDefinition.HouseholdQuestionnaire)) {
		initialFormData = {
			...initialFormData,
			familyInformation: {
				civilStatus: CivilStatus.None,
				childrenCount: 0,
				housingStatus: null,
				childrenAges: [],
			},
		}
	}

	if (
		config.moduleIds.includes(
			ModuleDefinition.HouseholdSingleCohabitantQuestionnaire,
		)
	) {
		initialFormData = {
			...initialFormData,
			householdSingleCohabitantInformation: {},
		}
	}

	if (
		config.moduleIds.some(
			(x) =>
				x === ModuleDefinition.AssetQuestionnaire ||
				x === ModuleDefinition.CarExtendedQuestionnaire,
		)
	) {
		initialFormData = {
			...initialFormData,
			...(config.loanType === ClientInputsType.HomeLoan
				? {
						homeLenderInputs: {
							acquiringExtraHome: null,
							hasExpensesToMultipleHomes: null,
							insurance: 0,
						},
					}
				: {
						carLenderInputs: {
							acquiringExtraCar: null,
							currentMonthlyCarCost: null,
							carStatus: CarStatus.None,
							redemptionAmount: null,
							hasHadCarBefore: null,
							hasExpensesToMultipleCars: null,
							insurance: null,
						},
					}),
		}
	}

	if (config.moduleIds.includes(ModuleDefinition.MbfQuestionnaire)) {
		initialFormData = {
			...initialFormData,
			mbfInputs: {
				employmentStatus: EmploymentStatus.None,
				housingChangesLast5Years: 0,
				isEmploymentAnnounced: null,
				selfEmployedSince: null,
			},
		}
	}

	const getDynamicSteps = (currentValues: DefaultConsumerInputs) => {
		const steps: QuestionnaireStepWithType[] = []
		const formsAnswered = config.questionnaireStatus
			.filter((form) => form.answered)
			.map((form) => form.moduleDefinitionType)
		const formsToSkip = config.questionnaireStatus
			.filter((form) => form.shouldSkip)
			.map((form) => form.moduleDefinitionType)

		config.moduleIds.forEach((module) => {
			if (
				!isQuestionnaireModuleDefinition(module) ||
				formsAnswered?.includes(module) ||
				formsToSkip?.includes(module)
			) {
				return
			}

			if (module === ModuleDefinition.HouseholdSingleCohabitantQuestionnaire) {
				const currentCivilStatus =
					currentValues.familyInformation?.civilStatus !== undefined &&
					currentValues.familyInformation?.civilStatus !== CivilStatus.None
						? currentValues.familyInformation?.civilStatus
						: config.clientInputCivilStatus

				if (currentCivilStatus === CivilStatus.Married) {
					steps.push({
						type: module as ModuleDefinitionQuestionnaireType,
						...getQuestionnaireStep(
							module as ModuleDefinitionQuestionnaireType,
							config.loanType,
						),
					})
				}
			} else {
				steps.push({
					type: module as ModuleDefinitionQuestionnaireType,
					...getQuestionnaireStep(
						module as ModuleDefinitionQuestionnaireType,
						config.loanType,
					),
				})
			}
		})

		if (steps.length === 0) {
			eventHubService.onEvent({
				source: 'questionnaireModule',
				topic: 'moduleCompleted',
				payload: { module: 'questionnaire', skipped: false },
			})
		}
		return steps
	}

	const onSubmit = async (values: DefaultConsumerInputs) => {
		const steps = getDynamicSteps(values)
		const isLastStep = step === steps.length - 1
		const { type } = steps[step]!
		const questionnaireGroupType = getSectionFromQuestionnaireType(type)
		if (isLastStepInSection(step, questionnaireGroupType, steps)) {
			trackingService.trackEvent('QuestionnaireSectionFinished', {
				type: questionnaireGroupType,
				isLastStep,
			})
		}

		if (
			!steps.find(
				(_step) =>
					_step.type ===
					ModuleDefinitionQuestionnaire.HouseholdSingleCohabitantQuestionnaire,
			)
		) {
			values.householdSingleCohabitantInformation = undefined
		}

		const transformConsumerInputs = (
			values: DefaultConsumerInputs,
		): DefaultConsumerInputs => {
			const transformedConsumerInputs: DefaultConsumerInputs = {}

			if (values.carLenderInputs !== undefined) {
				transformedConsumerInputs.carLenderInputs = {
					...values.carLenderInputs,
					insurance: getNegativeValue(values.carLenderInputs.insurance),
					currentMonthlyCarCost: getNegativeValue(
						values.carLenderInputs.currentMonthlyCarCost,
					),
				}
			}

			if (values.homeLenderInputs !== undefined) {
				transformedConsumerInputs.homeLenderInputs = {
					...values.homeLenderInputs,
					insurance: getNegativeValue(values.homeLenderInputs.insurance),
				}
			}

			return transformedConsumerInputs
		}

		if (isLastStep) {
			try {
				setIsSaving(true)
				await finish({
					...values,
					...transformConsumerInputs(values),
				})
			} catch (error) {
				console.error('err', error)
				setIsFailure(true)
				setIsSaving(false)
			}
			return
		}

		setStep(step + 1)
	}

	const onPreviousStep = () => {
		if (step != 0) {
			setStep(step - 1)
		}
	}
	const validate = async (values: DefaultConsumerInputs) => {
		setIsValidating(true)
		const steps = getDynamicSteps(values)
		let errors: any = {}
		await Promise.all(
			steps.slice(0, step + 1).map(async (x: any) => {
				errors = await x.validation({
					values,
					errors,
					regionId: config.regionId,
					validateCprAgainstServer: config.validateCprAgainstServer,
				})
			}),
		)
		setIsValidating(false)
		return errors
	}

	if (isSaving) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	return (
		<>
			<ConsumerInputError isFailure={isFailure} />
			<Formik
				enableReinitialize
				initialValues={initialFormData}
				onSubmit={onSubmit}
				validate={validate}
				validateOnMount
			>
				{(formProps) => {
					const steps = getDynamicSteps(formProps.values)
					const isDisabled = Object.keys(formProps.errors).length > 0

					return (
						<Form
							style={{
								height: '100%',
							}}
						>
							<FlowPage>
								{renderStep(
									steps.map((x) => x.component),
									step,
									formProps,
									(config.regionId as Region) ?? Region.Denmark,
									isValidating,
								)}
								<FlowPage.Footer
									backButton={
										step != 0 ? (
											<FlowPage.BackButton
												onClick={onPreviousStep}
												ariaLabel={t('btn-back')}
											/>
										) : undefined
									}
									actions={
										<FlowPage.Cta
											disabled={formProps.isSubmitting || isDisabled}
											onClick={() => {
												formProps.handleSubmit()
											}}
										>
											{t('consumerInputsPage-continue-text')}
										</FlowPage.Cta>
									}
								/>
							</FlowPage>
						</Form>
					)
				}}
			</Formik>
		</>
	)
}
